import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import dayjs from "dayjs";
import axios from "axios";
import Select from "react-select";

Modal.setAppElement("#root");
const ADMIN_UPDATE_REASON = "Admin Update";

const PositionHistoryModal = ({
  isOpen,
  onClose,
  onSave,
  record,
  userId,
  BASE_URL,
  canUseAdminUpdate = false,
}) => {
  const [formData, setFormData] = useState({
    ph_position_id: "",
    ph_emp_category: "",
    ph_start_date: "",
    ph_end_date: "",
    ph_reason_for_change: "",
    ph_note: "",
  });

  const [categories, setCategories] = useState([]);
  const [positions, setPositions] = useState([]);
  const [orgUnits, setOrgUnits] = useState([]);
  const [positionTypes, setPositionTypes] = useState([]);

  const reasonOptions = [
    { value: "API Transfer", label: "API Transfer" },
    { value: "Driver Rebuttal", label: "Driver Rebuttal" },
    { value: "Transfer", label: "Transfer" },
    ...(canUseAdminUpdate
      ? [{ value: ADMIN_UPDATE_REASON, label: ADMIN_UPDATE_REASON }]
      : []),
  ];

  // ✅ Load categories, org units, position types
  useEffect(() => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    };

    const fetchDropdowns = async () => {
      try {
        const [catRes, orgRes, typeRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/dropdowns/categories`, { headers }),
          axios.get(`${BASE_URL}/api/dropdowns/org-units`, { headers }),
          axios.get(`${BASE_URL}/api/dropdowns/position-types`, { headers }),
        ]);
        setCategories(catRes.data);
        setOrgUnits(orgRes.data);
        setPositionTypes(typeRes.data);
      } catch (err) {
        console.error("❌ Failed to load dropdowns:", err);
      }
    };
    fetchDropdowns();
  }, [BASE_URL]);

  // ✅ Load positions by category
  useEffect(() => {
    const fetchPositions = async () => {
      if (!formData.ph_emp_category) {
        setPositions([]);
        return;
      }
      try {
        const headers = {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        };
        const res = await axios.get(
          `${BASE_URL}/api/positions/by-category/${formData.ph_emp_category}`,
          { headers }
        );
        setPositions(res.data);
      } catch (err) {
        console.error("❌ Failed to fetch positions:", err);
      }
    };
    fetchPositions();
  }, [formData.ph_emp_category, BASE_URL]);

  // ✅ Initialize form values
  useEffect(() => {
    if (record) {
      setFormData({
        ph_position_id: record.ph_position_id || "",
        ph_emp_category: record.ph_emp_category || "",
        ph_start_date: record.ph_start_date
          ? dayjs(record.ph_start_date).format("YYYY-MM-DD")
          : "",
        ph_end_date: record.ph_end_date
          ? dayjs(record.ph_end_date).format("YYYY-MM-DD")
          : "",
        ph_reason_for_change: record.ph_reason_for_change || "",
        ph_note: record.ph_note || "",
      });
    } else {
      setFormData({
        ph_position_id: "",
        ph_emp_category: "",
        ph_start_date: dayjs().format("YYYY-MM-DD"),
        ph_end_date: "",
        ph_reason_for_change: "",
        ph_note: "",
      });
    }
  }, [record]);

  // ✅ Build position dropdown with readable names
  const positionOptions = positions.map((pos) => {
    const orgName =
      orgUnits.find((o) => o.ou_id === parseInt(pos.p_org_unit))?.ou_name ||
      "Unknown Unit";
    const typeName =
      positionTypes.find((pt) => pt.pt_id === parseInt(pos.p_position_type))
        ?.pt_name || "Unknown Type";

    const city =
      typeof pos.p_contact_info === "string"
        ? (() => {
            try {
              return JSON.parse(pos.p_contact_info)?.city || "";
            } catch {
              return "";
            }
          })()
        : pos.p_contact_info?.city || "";

    const label = `${pos.p_position_title} - ${orgName} - ${typeName}${
      city ? ` - ${city}` : ""
    }`;

    const sub = `Pos No: ${pos.p_position_number || "N/A"}`;

    return {
      value: pos.p_id,
      label: (
        <div style={{ lineHeight: "1.3" }}>
          <div>{label}</div>
          <small className="text-muted">{sub}</small>
        </div>
      ),
    };
  });

  // ✅ Handlers
  const handleSelectChange = (name, option) => {
    setFormData({ ...formData, [name]: option ? option.value : "" });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async () => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    };
    try {
      if (record) {
        await axios.put(
          `${BASE_URL}/api/status-history/position/${record.ph_id}`,
          formData,
          { headers }
        );
      } else {
        await axios.post(
          `${BASE_URL}/api/status-history/position/add`,
          { ...formData, ph_userid: userId },
          { headers }
        );
      }
      onSave?.();
      onClose?.();
    } catch (err) {
      console.error("❌ Failed to save position history:", err);
      alert(err.response?.data?.message || "Failed to save position history");
    }
  };

  const handleDelete = async () => {
    if (!record) return;
    if (!window.confirm("Are you sure you want to delete this record?")) return;
    const headers = {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    };
    try {
      await axios.delete(
        `${BASE_URL}/api/status-history/position/${record.ph_id}`,
        {
          headers,
        }
      );
      alert("🗑 Position history deleted successfully");
      onSave?.();
      onClose?.();
    } catch (err) {
      console.error("❌ Failed to delete position history:", err);
      alert("Failed to delete record");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="status-modal"
      overlayClassName="status-overlay"
    >
      <div className="scm-form">
        <h4 className="mb-3">
          {record ? "Edit Position History" : "Add Position History"}
        </h4>

        {/* 🧩 Employment Category */}
        <div className="scm-field">
          <label className="scm-label">Employment Category</label>
          <Select
            value={
              categories.find(
                (c) => c.c_id === Number(formData.ph_emp_category)
              )
                ? {
                    value: formData.ph_emp_category,
                    label:
                      categories.find(
                        (c) => c.c_id === Number(formData.ph_emp_category)
                      )?.c_name || "Select Category",
                  }
                : null
            }
            options={categories.map((c) => ({
              value: c.c_id,
              label: c.c_name,
            }))}
            onChange={(opt) => handleSelectChange("ph_emp_category", opt)}
            placeholder="Select Category"
            isClearable
            isSearchable
          />
        </div>

        {/* 🧩 Position */}
        <div className="scm-field">
          <label className="scm-label">Position</label>
          <Select
            value={
              positionOptions.find(
                (p) => String(p.value) === String(formData.ph_position_id)
              ) || null
            }
            options={positionOptions}
            onChange={(opt) => handleSelectChange("ph_position_id", opt)}
            placeholder="Select Position"
            isDisabled={!formData.ph_emp_category}
            isClearable
            isSearchable
            styles={{
              menu: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
        </div>

        {/* 🧩 Start Date */}
        <div className="scm-field">
          <label className="scm-label">Start Date</label>
          <input
            type="date"
            className="scm-input"
            name="ph_start_date"
            value={formData.ph_start_date}
            onChange={handleChange}
          />
        </div>

        {/* 🧩 End Date */}
        <div className="scm-field">
          <label className="scm-label">End Date</label>
          <input
            type="date"
            className="scm-input"
            name="ph_end_date"
            value={formData.ph_end_date}
            onChange={handleChange}
          />
        </div>

        {/* 🧩 Reason for Change */}
        <div className="scm-field">
          <label className="scm-label">Reason for Change</label>
          <Select
            value={
              reasonOptions.find(
                (r) => r.value === formData.ph_reason_for_change
              ) || null
            }
            options={reasonOptions}
            onChange={(opt) => handleSelectChange("ph_reason_for_change", opt)}
            placeholder="Select Reason"
            isClearable
          />
        </div>

        {/* 🧩 Note */}
        <div className="scm-field">
          <label className="scm-label">Note</label>
          <textarea
            className="scm-textarea"
            name="ph_note"
            rows="3"
            value={formData.ph_note}
            onChange={handleChange}
          />
        </div>

        {/* 🧩 Actions */}
        <div className="scm-actions d-flex justify-content-between">
          <button
            type="button"
            className="scm-btn scm-btn-cancel"
            onClick={onClose}
          >
            <i className="fa fa-ban me-1" />
            Cancel
          </button>

          <div>
            {record && (
              <button
                type="button"
                className="scm-btn scm-btn-danger me-2"
                onClick={handleDelete}
              >
                <i className="fa fa-trash me-1" />
                Delete
              </button>
            )}
            <button
              type="button"
              className="scm-btn scm-btn-save"
              onClick={handleSubmit}
            >
              <i className="fa fa-save me-1" />
              Save
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PositionHistoryModal;
